// DEPENDENCIES
import { FormikValues } from "formik";
import * as Sentry from "@sentry/react";

// TYPES
import { Step } from "../../types";

// UTILS
import { onUpdateInfoStep } from "../../reducers/infoStepReducer";
import {
  onUpdateFormData,
  onUpdateStep,
} from "../../reducers/onboardingDataReducer";
import {
  getDniFromCuit,
  getPartnerCompanyType,
  isJuridicalCuit,
  isNextStepCompleted,
} from "../../utils";
import { getCompanyData } from "../../utils/companies";
import { authEmail } from "../../connectors/connectors";
import {
  updateDraftBase,
  updateSignatureRequestId,
} from "../../reducers/authReducer";
import { getDraftBase } from "../../utils/draftBase";
import { fieldsFrom } from "../../inputs";
import { AppDispatch } from "../../app/store";
import { logAnalyticsAndHotjarEvent } from "../../main";

const basicInformationJuridicalStep = (): Omit<Step, "step"> => ({
  name: "Cuenta empresarial",
  titleOfStep: "Información básica",
  disableBackNavigation: isNextStepCompleted,
  fields: fieldsFrom(
    "emailWarningAlert",
    "cuitPJ",
    ["cuitPF", { label: "CUIT/CUIL del representante" }],
    "email",
    "referredBool",
    "referrer",
    "unifiedLabel",
  ),
  onContinue: async (values: FormikValues, dispatch) => {
    if (!dispatch || !values) throw new Error("Error: dispatch not found");

    //To prevent failed loop
    dispatch(onUpdateInfoStep(null));

    const {
      businessInformation: { cuit },
    } = values;

    return !isJuridicalCuit(cuit)
      ? handleInvalidCuit(dispatch)
      : await createOnboarding(dispatch, values);
  },
});

const handleInvalidCuit = (dispatch: AppDispatch) => {
  dispatch(onUpdateInfoStep(null));
  dispatch(onUpdateStep("information-step"));
  throw new Error("CUIT no válido");
};

const createOnboarding = async (
  dispatch: AppDispatch,
  values: FormikValues,
) => {
  const { email } = values;
  const partnerCompanyType = getPartnerCompanyType(values);
  const draftBase = getDraftBase(values);

  const res = await authEmail({
    email,
    es: true,
    companyType: getCompanyData().companyName,
    ...(partnerCompanyType ? { partnerCompanyType } : {}),
    draftBase,
  });

  dispatch(updateSignatureRequestId(res.data.signatureRequestId));
  dispatch(updateDraftBase(draftBase));

  if (email) Sentry.setUser({ email });

  const identityDocument = getDniFromCuit(values.cuit);
  if (!identityDocument) return;

  dispatch(onUpdateFormData({ identityDocument }));

  logAnalyticsAndHotjarEvent("step_information_basic_completed_pj");

  return { newValuesToForm: { identityDocument } };
};

export default basicInformationJuridicalStep;
